/**
 * Phase 6 — Redesign feature flag
 *
 * As of PR #57 (Phase 6 final), the redesign is ON by default.
 * Returns true unless explicitly disabled. Sources, in priority order:
 *   1. URL query param ?redesign=0  (force off, useful for QA-ing legacy)
 *   2. URL query param ?redesign=1  (force on — redundant now but kept for sharing)
 *   3. import.meta.env.VITE_REDESIGN === "0"  (build-time opt-out)
 *   4. Default: ON
 *
 * Result is memoised at module load — flips require a reload (which is what
 * we want; mid-session toggling would create style cache mess).
 */

const readUrlOverride = (): boolean | null => {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("redesign");
    if (v === "1" || v === "true" || v === "on") return true;
    if (v === "0" || v === "false" || v === "off") return false;
  } catch {
    /* ignore (URL parse) */
  }
  return null;
};

const computed = (() => {
  const override = readUrlOverride();
  if (override !== null) return override;
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  // Default-on: only opt out if explicitly set to "0".
  return env?.VITE_REDESIGN !== "0";
})();

export const isRedesignEnabled = (): boolean => computed;

/** Apply / remove the .redesign class on <html> based on the flag. Call once at boot. */
export const installRedesignClass = (): void => {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  if (computed) {
    html.classList.add("redesign");
  } else {
    html.classList.remove("redesign");
  }
};
